import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Button } from '../components/ui/button';
import { Package, Truck, CheckCircle, Clock, MapPin, FileText, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function OrderTracking() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrderTracking();
  }, [orderNumber]);

  const fetchOrderTracking = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/orders/track/${orderNumber}`);
      
      if (!response.ok) {
        throw new Error('Order not found');
      }
      
      const data = await response.json();
      setOrder(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      processing: 'bg-blue-500',
      shipped: 'bg-purple-500',
      delivered: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Clock className="w-6 h-6" />,
      processing: <Package className="w-6 h-6" />,
      shipped: <Truck className="w-6 h-6" />,
      delivered: <CheckCircle className="w-6 h-6" />,
      cancelled: <Clock className="w-6 h-6" />
    };
    return icons[status] || <Clock className="w-6 h-6" />;
  };

  const statusSteps = ['pending', 'processing', 'shipped', 'delivered'];

  const getCurrentStepIndex = (status) => {
    return statusSteps.indexOf(status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Order Not Found</CardTitle>
            <CardDescription>
              We couldn't find an order with number: {orderNumber}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (!order) return null;

  const currentStepIndex = getCurrentStepIndex(order.status);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header with Action Buttons */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Track Your Order</h1>
            <p className="text-gray-600">Order Number: <span className="font-semibold">{order.order_number}</span></p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              onClick={() => navigate(`/invoice/${order.order_number}`)}
              className="bg-orange-600 hover:bg-orange-700 flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              View Invoice
            </Button>
          </div>
        </div>

        {/* Order Status Timeline */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Order Status</CardTitle>
            <CardDescription>
              Current Status: <Badge className={getStatusColor(order.status)}>
                {order.status.toUpperCase()}
              </Badge>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Progress Bar */}
            <div className="relative">
              <div className="flex items-center justify-between">
                {statusSteps.map((step, index) => (
                  <div key={step} className="flex flex-col items-center flex-1">
                    {/* Icon */}
                    <div
                      className={`rounded-full p-3 ${
                        index <= currentStepIndex
                          ? 'bg-orange-600 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}
                    >
                      {getStatusIcon(step)}
                    </div>
                    {/* Label */}
                    <p className={`mt-2 text-sm font-medium ${
                      index <= currentStepIndex ? 'text-orange-600' : 'text-gray-400'
                    }`}>
                      {step.charAt(0).toUpperCase() + step.slice(1)}
                    </p>
                    
                    {/* Connecting Line */}
                    {index < statusSteps.length - 1 && (
                      <div className="absolute top-6 w-full left-0 flex items-center" style={{ zIndex: -1 }}>
                        <div className="flex-1 mx-16">
                          <div
                            className={`h-1 ${
                              index < currentStepIndex ? 'bg-orange-600' : 'bg-gray-200'
                            }`}
                            style={{ marginLeft: index === 0 ? '50%' : '0', marginRight: index === statusSteps.length - 2 ? '50%' : '0' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Tracking Number */}
            {order.tracking_number && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900">Tracking Number</p>
                <p className="text-lg font-bold text-blue-700">{order.tracking_number}</p>
              </div>
            )}

            {/* Estimated Delivery */}
            {order.estimated_delivery && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg flex items-center">
                <MapPin className="w-5 h-5 text-green-700 mr-2" />
                <div>
                  <p className="text-sm font-medium text-green-900">Estimated Delivery</p>
                  <p className="text-lg font-bold text-green-700">{order.estimated_delivery}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status History */}
        {order.status_history && order.status_history.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Order History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {order.status_history.slice().reverse().map((history, index) => (
                  <div key={index} className="flex items-start">
                    <div className={`rounded-full p-2 ${getStatusColor(history.status)} mr-4`}>
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{history.status.charAt(0).toUpperCase() + history.status.slice(1)}</p>
                      <p className="text-sm text-gray-600">{history.note}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(history.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Details */}
        <Card>
          <CardHeader>
            <CardTitle>Order Details</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Customer Info */}
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Customer Information</h3>
              <p className="text-gray-600">
                {order.customer_info.first_name} {order.customer_info.last_name}
              </p>
              <p className="text-gray-600">{order.customer_info.email}</p>
            </div>

            <Separator className="my-4" />

            {/* Items */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Items</h3>
              <div className="space-y-2">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-gray-600">
                        {item.brand} × {item.quantity}
                      </p>
                    </div>
                    <p className="font-semibold">OMR {(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="my-4" />

            {/* Total */}
            <div className="flex justify-between items-center text-lg font-bold">
              <span>Total</span>
              <span className="text-orange-600">OMR {order.total.toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default OrderTracking;
